import { BackButton } from "@/components/ui/BackButton";
import { Button, Card, Flex, Grid, message, Typography } from "antd";
import {
  normalizeStatus,
  statusLabelMap,
  statusThemeMap,
  STEPS,
} from "@/features/public/utils/status.utils";
import { ArrowDownToLine } from "lucide-react";
import type { CheckRecordResponse, RegistrationResponse } from "./types/public";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

type SubmittedStatusProps = {
  onBack: () => void;
  record: RegistrationResponse | CheckRecordResponse;
  onReapply?: () => void;
};

export function SubmittedStatus({
  onBack,
  record,
  onReapply,
}: SubmittedStatusProps) {
  const screens = useBreakpoint();

  const PDF_URL = "/BORANG-PENGESAHAN-ERMASTAUTIN_3882.pdf";

  const rawStatus = normalizeStatus(record.statusKey ?? undefined);
  const shouldShowNextStep = rawStatus === "pending";

  const statusLabel = statusLabelMap[rawStatus] ?? "-";
  const statusTheme = statusThemeMap[rawStatus] ?? {
    headerBg: "#F5F5F5",
    text: "#272728",
  };

  const canReapply =
    rawStatus === "rejected" &&
    (!("isReapplyAllowed" in record) || Boolean(record.isReapplyAllowed));

  const handleDownloadForm = () => {
    const w = window.open(PDF_URL, "_blank", "noopener,noreferrer");
    if (!w) {
      message.warning("Pelayar menyekat pembukaan PDF. Sila benarkan pop-up.");
    }
  };

  return (
    <>
      <Title
        level={2}
        style={{
          marginTop: 16,
          marginBottom: 0,
          textAlign: "center",
          fontSize: screens.sm ? 32 : 24,
          fontWeight: 700,
          color: "var(--color-primary)",
        }}
      >
        Pendaftaran
      </Title>

      <div className="mt-12">
        <BackButton onClick={onBack} />

        <div className="mt-8">
          <Card
            variant="borderless"
            title={"Status: " + statusLabel}
            styles={{
              header: {
                textAlign: "center",
                fontWeight: 700,
                fontSize: screens.sm ? 24 : 18,
                backgroundColor: statusTheme.headerBg,
                color: statusTheme.text,
              },
            }}
            className="shadow-xl!"
          >
            <Text
              style={{
                color: "var(--color-primary)",
                fontSize: screens.sm ? 24 : 18,
                fontWeight: 700,
                display: "block",
                marginBottom: "40px",
              }}
            >
              Maklumat Peribadi
            </Text>

            <Flex vertical>
              <Text
                style={{
                  color: "#666",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Nama
              </Text>
              <Text strong style={{ fontSize: screens.sm ? 18 : 16 }}>
                {record.name}
              </Text>
            </Flex>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-10 mt-4 sm:mt-8">
              <Flex vertical>
                <Text
                  style={{
                    color: "#666",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Nombor MYKAD / Pasport
                </Text>
                <Text strong style={{ fontSize: screens.sm ? 18 : 16 }}>
                  <span style={{ letterSpacing: 1 }}>
                    {record.maskedIdentityNo}
                  </span>
                </Text>
              </Flex>

              <Flex vertical>
                <Text
                  style={{
                    color: "#666",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Taraf penduduk
                </Text>
                <Text strong style={{ fontSize: screens.sm ? 18 : 16 }}>
                  {record.categoryName}
                </Text>
              </Flex>

              {"rejectedRemark" in record &&
                record.statusKey === "rejected" && (
                  <Flex vertical>
                    <Text
                      style={{
                        color: "#666",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Sebab
                    </Text>
                    <Text strong style={{ fontSize: screens.sm ? 18 : 16 }}>
                      {record.rejectedRemark}
                    </Text>
                  </Flex>
                )}
            </div>

            {canReapply && (
              <Button
                htmlType="button"
                variant="solid"
                size="large"
                style={{ width: "100%", marginTop: 40 }}
                onClick={onReapply}
              >
                Mohon semula
              </Button>
            )}
          </Card>

          {shouldShowNextStep && (
            <Card
              style={{
                marginTop: "40px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Title
                level={3}
                style={{
                  fontWeight: 700,
                  fontSize: screens.sm ? 24 : 18,
                  color: "var(--color-primary)",
                  marginBottom: 24,
                }}
              >
                LANGKAH SETERUSNYA
              </Title>

              <ol className="list-decimal pl-5 space-y-4">
                {STEPS.map((step, index) => (
                  <li
                    className="text-sm font-medium text-[#272728]"
                    key={index}
                  >
                    {step}
                  </li>
                ))}
              </ol>

              <Paragraph
                style={{
                  paddingLeft: "1.25rem",
                  paddingTop: "0.5rem",
                  color: "var(--color-primary)",
                  fontWeight: 500,
                }}
              >
                <Text strong style={{ color: "var(--color-primary)" }}>
                  Lembaga Pembangunan Tioman:
                </Text>
                Pejabat Pentadbiran Pulau Tioman, Tingkat Atas, Kompleks Bebas
                Cukai, Kampung Tekek, Pulau Tioman, 26800 Rompin, Pahang
              </Paragraph>

              <Button
                htmlType="button"
                variant="solid"
                size="large"
                style={{ width: "100%" }}
                className="mt-6"
                icon={<ArrowDownToLine size={18} aria-hidden="true" />}
                onClick={handleDownloadForm}
              >
                Muat Turun Borang
              </Button>
            </Card>
          )}
        </div>

        <p className="text-sm text-sky-100 font-medium text-center my-16">Sebarang pertanyaan berkenaan pendaftaran, boleh hubungi pejabat Lembaga Pembangunan Tioman (TDA) di talian 019-9283179</p>
      </div>
    </>
  );
}
